import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import moment from 'moment';

import { IOverdueSexOffender, SexOffenderApi } from '@/api';
import { DATE_FORMAT } from '@/config';
import { LocalStorageItems, LocalStoragePrefix } from '@/constants';
import { RiskLevelLabels } from '@/models';
import {
  useNotificationStore,
  useSexOffendersStore,
  useUserStore,
} from '@/store';

interface ISexOffenderDueNotificationState {
  entries?: Record<
    string,
    {
      lastShownDate?: string;
      lastShownLoginSessionId?: string | null;
    }
  >;
}

const getNotificationStorageKey = (userId: string) =>
  `${LocalStoragePrefix.SexOffenderDueNotification}:${userId}`;

const getNotificationState = (
  userId: string,
): ISexOffenderDueNotificationState | null => {
  try {
    const value = localStorage.getItem(getNotificationStorageKey(userId));
    return value
      ? (JSON.parse(value) as ISexOffenderDueNotificationState)
      : null;
  } catch {
    return null;
  }
};

const saveNotificationState = (
  userId: string,
  state: ISexOffenderDueNotificationState,
) => {
  try {
    localStorage.setItem(
      getNotificationStorageKey(userId),
      JSON.stringify(state),
    );
  } catch {
    // Ignore localStorage errors
  }
};

const buildOverdueFingerprint = (item: IOverdueSexOffender) =>
  `${item._id}|${item.sexOffenderId || ''}|${item.nextDueDate || ''}|${
    item.riskLevel || ''
  }`;

export const SexOffenderDueNotification = () => {
  const navigate = useNavigate();
  const { user } = useUserStore();
  const { dueNotificationRefreshKey } = useNotificationStore();
  const { setCheckinFrequency } = useSexOffendersStore();
  const [offenders, setOffenders] = useState<IOverdueSexOffender[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const userId = user?._id;
    if (!userId) return;

    const fetchOverdue = async () => {
      try {
        const { data } = await SexOffenderApi.getOverdue();
        if (data.settings?.checkinFrequency) {
          setCheckinFrequency(data.settings.checkinFrequency);
        }
        if (data.notifications.length > 0) {
          const today = moment().format('YYYY-MM-DD');
          const loginSessionId = localStorage.getItem(
            LocalStorageItems.SexOffenderDueLoginSessionId,
          );
          const notificationState = getNotificationState(userId);
          const entryStateMap = notificationState?.entries || {};

          const offendersToShow = data.notifications.filter((entry) => {
            const entryFingerprint = buildOverdueFingerprint(entry);
            const seenEntry = entryStateMap[entryFingerprint];
            const hasSeenToday = seenEntry?.lastShownDate === today;
            const hasSeenThisLogin =
              !!loginSessionId &&
              seenEntry?.lastShownLoginSessionId === loginSessionId;

            return !(hasSeenToday && hasSeenThisLogin);
          });

          if (offendersToShow.length === 0) {
            return;
          }

          setOffenders(offendersToShow);
          setCurrentIndex(0);
          setOpen(true);

          const updatedEntryMap = { ...entryStateMap };
          offendersToShow.forEach((entry) => {
            const entryFingerprint = buildOverdueFingerprint(entry);
            updatedEntryMap[entryFingerprint] = {
              lastShownDate: today,
              lastShownLoginSessionId: loginSessionId,
            };
          });

          saveNotificationState(userId, {
            entries: updatedEntryMap,
          });
        }
      } catch {
        // fail silently - not critical
      }
    };

    fetchOverdue();
  }, [user?._id, dueNotificationRefreshKey]);

  const current = offenders[currentIndex];

  const handleClose = () => {
    if (currentIndex < offenders.length - 1) {
      setCurrentIndex((i) => i + 1);
    } else {
      setOpen(false);
    }
  };

  const handleViewRecord = () => {
    navigate(`/tools/sex-offenders/${current._id}`);
    handleClose();
  };

  if (!current) return null;

  const riskLevel =
    current.riskLevel !== undefined && current.riskLevel !== null
      ? RiskLevelLabels[current.riskLevel] ?? '-'
      : '-';
  const nextDue = current.nextDueDate
    ? moment(current.nextDueDate).format(DATE_FORMAT)
    : '-';
  const daysFromNow = current.nextDueDate
    ? moment(current.nextDueDate)
        .startOf('day')
        .diff(moment().startOf('day'), 'days')
    : null;
  const isOverdue = daysFromNow !== null && daysFromNow < 0;
  const isCurrentDay = daysFromNow === 0;
  const dayCount = daysFromNow !== null ? Math.abs(daysFromNow) : 0;
  const suffix =
    daysFromNow === null
      ? ''
      : isOverdue
      ? `${dayCount} day${dayCount !== 1 ? 's' : ''} overdue`
      : `${daysFromNow} day${daysFromNow !== 1 ? 's' : ''} remaining`;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          Sex Offender Check-In Due
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body1" gutterBottom>
          <strong>Sex Offender Name:</strong> {current.personName}
          {current.sexOffenderId ? ` · ${current.sexOffenderId}` : ''}
        </Typography>
        <Typography variant="body1" gutterBottom>
          <strong>Risk Level:</strong> {riskLevel}
        </Typography>
        <Typography variant="body1">
          <strong>Last Check-In:</strong>{' '}
          {current.nextDueDate ? (
            <Box
              component="span"
              sx={{ color: isOverdue ? 'error.main' : 'inherit' }}
            >
              <Box
                component="span"
                sx={{
                  color: isCurrentDay && !isOverdue ? 'error.main' : 'inherit',
                }}
              >
                {nextDue}
              </Box>{' '}
              ({suffix})
            </Box>
          ) : (
            '-'
          )}
        </Typography>
        {offenders.length > 1 && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: 'block' }}
          >
            {currentIndex + 1} of {offenders.length} overdue check-ins
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={handleClose}>
          Close
        </Button>
        <Button variant="contained" color="error" onClick={handleViewRecord}>
          View record
        </Button>
      </DialogActions>
    </Dialog>
  );
};
